<script setup lang="ts">
/**
 * Renders one ```mermaid block as a diagram. Mermaid is loaded lazily and
 * initialised with securityLevel: "strict" — script tags, click handlers
 * and inline HTML in node labels are stripped, so an LLM- or
 * document-sourced diagram can't inject markup. We additionally
 * DOMPurify-sanitise the produced SVG before injecting it (defence in
 * depth). On any parse/render error we fall back to showing the raw
 * mermaid source in a <pre> — never a blank box or a thrown error.
 */
import { onMounted, ref, watch } from "vue";
import DOMPurify from "dompurify";

const props = defineProps<{ src: string; idSeed: number }>();

const svg = ref<string>("");
const failed = ref(false);

async function render() {
  try {
    const mermaid = (await import("mermaid")).default;
    mermaid.initialize({
      startOnLoad: false,
      securityLevel: "strict",
      theme: document.documentElement.classList.contains("dark")
        ? "dark"
        : "default",
    });
    // id must be unique + valid; seed comes from the segment index.
    const { svg: out } = await mermaid.render(`mmd-${props.idSeed}`, props.src);
    svg.value = DOMPurify.sanitize(out, {
      USE_PROFILES: { svg: true, svgFilters: true },
    });
    failed.value = false;
  } catch (err) {
    console.error("[MermaidBlock] render failed", err);
    failed.value = true;
    svg.value = "";
  }
}

onMounted(render);
watch(() => props.src, render);
</script>

<template>
  <div class="mermaidblk">
    <!-- eslint-disable-next-line vue/no-v-html — sanitised above -->
    <div v-if="!failed" class="mermaidblk__svg" v-html="svg"></div>
    <pre v-else class="mermaidblk__fallback"><code>{{ src }}</code></pre>
  </div>
</template>

<style scoped>
.mermaidblk {
  margin: 0.75rem 0;
  overflow-x: auto;
}
.mermaidblk__svg :deep(svg) {
  max-width: 100%;
  height: auto;
}
.mermaidblk__fallback {
  background: color-mix(in srgb, var(--color-ink) 6%, transparent);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 0.6rem;
  font-size: 0.8rem;
  overflow-x: auto;
}
</style>
